import React, { useState } from 'react';
import { Channel, ChannelMemberResponse, ChannelResponse, ChannelState } from 'stream-chat';
import { ChatAvatar } from './ChatAvatar';
import { FriendshipStatus, FriendshipStatusType } from '../../../models/FriendshipStatus';
import UserBadge from './UserBadge';
import { useAuth } from '../../../hooks/useAuth';
import { UserBadgeType } from '../../../models/UserBadgeType';
import { getRelativeDate } from '../../../utils/Utils';
import { ChatChannelUser } from '../../../models/ChatChannelUser';
import { ChatChannel } from '../../../models/ChatChannel';
import { ChatChannelPopUpMenu } from './ChatChannelPopUpMenu';
import TileList from './TileList';

interface ChannelData extends ChannelResponse {
  name?: string;
  userBadge?: UserBadgeType;
  isMuted?: boolean;
  friendshipStatus?: FriendshipStatus;
  image?: string;
  admins?: string[];
  status?: string;
  connectionId?: string;
  merchantCount?: number;
}

interface CustomChannelMemberResponse extends ChannelMemberResponse {
    user: {
      id: string;
      name?: string;
      image?: string;
      slug?: string;
      bio?: string;
      badge?: UserBadgeType;
      merchant?: boolean;
      connection?: any;
      banned?: boolean;
    };
  }
  
interface CustomChannelState extends ChannelState {
    members: Record<string, CustomChannelMemberResponse>;
}

export interface ChannelExtended extends Channel {
    data: ChannelData;
    state: CustomChannelState;
    member_count?: number;
    created_by?: {
      id: string;
    };
    status?: string;
    connectionId?: string;
}

interface ChatListTileProps {
  chatChannel: ChatChannel;
  onTap: () => void;
  isSelected: boolean;
}

export const ChatListTile: React.FC<ChatListTileProps> = ({
  chatChannel,
  onTap,
  isSelected,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { pleb } = useAuth();

  const getFriendshipStatusText = (status: FriendshipStatus): string | null => {
    if (status.isBlocked) {
        if(status.type === FriendshipStatusType.RequestReceived)
        {
            return 'AWAITING YOUR REPLY';
        }
        else if(status.type === FriendshipStatusType.RequestSent)
        {
            return 'WAITING FOR REPLY';
        }
        else 
        {
            return null;
        }
    }
    return null;
  };

  const getLastMessageText = (): string => {
    const lastMessage = chatChannel.lastMessage;

    if (!lastMessage) return '';

    // Stream system messages: plain text only (no author prefix / status)
    if (lastMessage.type === 'system') {
      return lastMessage.text || '';
    }

    const lastMessageAuthor = lastMessage.author;
    const currentUser = pleb;

    let prefix = '';

    if (currentUser?.id === lastMessageAuthor?.id) {
      prefix = 'You: ';
    } else if (!(chatChannel instanceof ChatChannelUser)) {
      prefix = `${lastMessageAuthor?.name}: `;
    }

    if(lastMessage.attachments)
    {
        const attachments = lastMessage.attachments.filter(
        attachment => attachment.type === 'image' || attachment.type === 'video'
        );
    
        if (attachments?.length > 0) {
        return `${prefix}${attachments.length === 1 ? '1 attachment' : `${attachments.length} attachments`}`;
        }

        if(lastMessage.attachments?.some(attachment => attachment.type === 'voiceRecording')){
          return `${prefix}Voice message`;
        }
    }

    if (lastMessage.attachments?.some(attachment => attachment.type === 'giphy')) {
      return `${prefix}GIF`;
    }

    return `${prefix}${lastMessage.text || ''}`;
  };

  const isPinned = chatChannel.channel.state?.membership.pinned_at !== undefined;

  return (
    <TileList 
      onTap={onTap} 
      isSelected={isSelected} 
      onChangeIsHovered={setIsHovered}
      childrenSwipped={<div className="flex-shrink-0 bg-brand-bg-4 flex items-center justify-center w-[65px]"><ChatChannelPopUpMenu channel={chatChannel} /></div>}
      widthSwipedPx={65}
    >
      <div className="flex items-stretch">
        <div className="mr-2">
          <ChatAvatar channel={chatChannel}/>
        </div>
        
        <div className="flex-1 truncate mr-2 flex flex-col justify-center">
          <div className="flex flex-row justify-between">
            <div className="flex items-center gap-1 truncate">
              <h3 className="font-semibold truncate text-sm">
                {chatChannel.name || ''}
              </h3>
              {chatChannel?.badge && <UserBadge badge={chatChannel.badge} />}
              {chatChannel.isMuted && <i className={`material-icons w-4 h-4 text-[16px]`}>volume_off</i>}
              {isPinned && <i className="material-symbols-outlined w-4 h-4 text-[16px]">keep</i>}
            </div>

            {
              !isHovered && (
                <span className={`text-sm ${chatChannel.unreadCount > 0 ? 'text-brand-orange' : 'text-brand-grey'}`}>{chatChannel.lastMessage?.date ? getRelativeDate(chatChannel.lastMessage.date) : ''}</span>
              )
            }
          </div>
          

          {getFriendshipStatusText(chatChannel.friendshipStatus) && (
            <div className="flex mt-0.5 mb-0.5">
              <span
                className={`px-4 py-1 rounded-full text-xs font-semibold ${
                  chatChannel.friendshipStatus.type === FriendshipStatusType.RequestReceived
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-200 text-gray-400 dark:bg-gray-900'
                }`}
              >
                {getFriendshipStatusText(chatChannel.friendshipStatus)}
              </span>
            </div>
          )}

          <div className="flex items-start justify-between gap-1 truncate mt-1">
            <p
              className={`truncate ${
                chatChannel.unreadCount > 0 ? 'font-normal' : 'font-normal'
              } text-sm text-brand-grey`}
            >
              {getLastMessageText()}
            </p>
            <div className="flex flex-row items-center gap-2">
              {!isHovered && chatChannel.unreadMentionsCount > 0 && (
                <div className="shrink-0 px-1 min-w-[20px] min-h-[20px] bg-brand-orange rounded-full flex items-center justify-center text-white text-xs font-semibold">@</div>
              )}
              {!isHovered && chatChannel.unreadCount > 0 && (
                <div className="shrink-0 px-1 min-w-[20px] min-h-[20px] bg-brand-orange rounded-full flex items-center justify-center text-white text-xs font-semibold">{chatChannel.unreadCount}</div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-between">
          {
            isHovered && (
              <div className="flex-grow flex items-center justify-center z-[10]">
                <ChatChannelPopUpMenu channel={chatChannel} />
              </div>
            )
          }
        </div>
      </div>
    </TileList>
  );
};